import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LightboxImages from "./UI/LightboxImages";
import {
  LoadingState,
  EmptyState,
  FullPageLoader,
  ErrorState,
} from "./UI/StateHandlers";
import EventImagesGrid from "./UI/EventImagesGrid";
import EventHeader from "./UI/EventHeader";
import Pagination from "./UI/Pagination";
import useGuestImages from "../../hooks/useGuestImages";
import useDeleteGuestImage from "../../hooks/useDeleteGuestImage";
import useUploadedImagesHook from "../../hooks/userUploadedImagesHook";
import "./UserUploadedImages.css";
import "./UI/Pagination.css";

const UserUploadedImages = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");

  // list of images
  const {
    images,
    loading,
    error,
    resetGuestImages: resetState,
    setImages,
    eventDetails,
    fetchPage,
    totalCount,
    currentPage,
    totalPages,
  } = useGuestImages({
    eventId,
  });

  // State for page navigation loading
  const [pageChanging, setPageChanging] = useState(false);

  // Wrapper function for fetchPage with loading state
  const handlePageChange = async (page) => {
    setPageChanging(true);
    await fetchPage(page);
    setPageChanging(false);
  };

  const handleDeleteImage = async (imageId) => {
    const success = await deleteGuestImage(imageId, eventId);
    if (success) {
      // Find the index of the deleted image
      const deletedImageIndex = images.findIndex((img) => img.id === imageId);

      // Check if this is the last image on the current page and we're not on page 1
      const isLastImageOnPage = images.length === 1 && currentPage > 1;

      // Manually remove the deleted image from the array
      setImages((prevImages) => {
        const newImages = prevImages.filter((img) => img.id !== imageId);

        // Set openMenuId to next or previous image if menu was open
        if (newImages.length > 0) {
          // Try next image first, then previous
          let nextPhotoIndex = 0;

          if (deletedImageIndex < newImages.length) {
            // Next image exists
            nextPhotoIndex = deletedImageIndex;
          } else if (deletedImageIndex > 0) {
            // Use previous image
            nextPhotoIndex = deletedImageIndex - 1;
          }

          setPhotoIndex(nextPhotoIndex);
        }
        if (!newImages?.length) {
          setOpen(false);
        }

        return newImages;
      });

      // If this was the last image on the page and we're not on page 1, go to previous page
      if (isLastImageOnPage) {
        const previousPage = currentPage - 1;
        fetchPage(previousPage);
      }
    }
  };
  // delete images hook
  const { deleteGuestImage, loading: deleting } = useDeleteGuestImage();

  const {
    photoIndex,
    setPhotoIndex,
    handleDeleteFromLightbox,
    buttonRef,
    open,
    setOpen,
    handleImageClick,
    openMenuId,
    setOpenMenuId,
    menuRef,
  } = useUploadedImagesHook({ images, handleDeleteImage });

  /* ---------- Loading ---------- */
  if (loading) {
    return <LoadingState />;
  }

  /* ---------- Error ---------- */
  if (error) {
    return (
      <ErrorState
        onRetry={() => {
          resetState();
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className="uploaded-images-container">
      {/* Header */}
      <EventHeader eventDetails={eventDetails} />

      <div className="header-divider" />

      {/* Grid */}
      {images?.length ? (
        <>
          <EventImagesGrid
            images={images}
            handleImageClick={handleImageClick}
            openMenuId={openMenuId}
            setOpenMenuId={setOpenMenuId}
            handleDeleteImage={handleDeleteImage}
            menuRef={menuRef}
            buttonRef={buttonRef}
          />
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            loading={loading || pageChanging}
            onPageChange={handlePageChange}
            currentCount={images.length}
          />
        </>
      ) : (
        <EmptyState />
      )}

      {/* Lightbox */}
      <LightboxImages
        open={open}
        close={() => setOpen(false)}
        photoIndex={photoIndex}
        images={images}
        changePhotoIndex={setPhotoIndex}
        onDelete={handleDeleteFromLightbox}
      />

      {/* Full-page transparent loader */}
      {deleting && <FullPageLoader />}
      
      {/* Page changing transparent loader */}
      {pageChanging && <FullPageLoader message="Loading photos…" />}
    </div>
  );
};

export default UserUploadedImages;
